import type {ErrorCorrectionLevel, Gradient as QRCodeStylingGradient, Options as QRCodeStylingOptions} from 'qr-code-styling';
import type {RGBA} from '@Utils/color.util';
import type {FillObject} from '@PosterWhiteboard/classes/fill.types';
import {getStoredFillColors, hasFill, hasGradientFill, hasLinearGradientFill} from '@PosterWhiteboard/classes/fill.types';
import type {QRCodeFileExtension, QRRenderConfig} from '@Libraries/qr-code/qr-code.types';
import {QR_BACKGROUND_CORNER_RADIUS_MAX, QR_DOT_TYPE_MAP, QR_EYE_BALL_TYPE_MAP, QR_EYE_FRAME_TYPE_MAP, QR_MARGIN_MAX} from '@Libraries/qr-code/qr-code.types';

/**
 * Default raster size, and the pixel space every derived measurement is expressed in. The poster item's `qrBorderRadius` is a fraction of half this value, so a caller that wants a
 * cheaper preview must pass a smaller `sizePx` rather than change this — lowering it here would desync the editor's border geometry from the raster it is drawn on.
 */
export const QR_GENERATION_SIZE_PX = 2048;

interface QRCodeStylingColorConfig {
  color?: string;
  gradient?: QRCodeStylingGradient;
}

const QR_MAX_MARGIN_FRACTION = 0.25;
const QR_LINEAR_GRADIENT_ROTATION = Math.PI / 2;
const QR_TRANSPARENT_BACKGROUND_COLOR = 'rgba(0,0,0,0)';
const QR_ERROR_CORRECTION_LEVEL: ErrorCorrectionLevel = 'M';
const QR_ERROR_CORRECTION_LEVEL_WITH_LOGO: ErrorCorrectionLevel = 'H';
/** Always clear the QR modules behind the logo: dots showing through transparent logo areas look broken, and EC level H plus the capped logo size keep the code scannable. */
const QR_LOGO_HIDE_BACKGROUND_DOTS = true;
/**
 * Gap between the logo and the QR dots, as a fraction of the code's width. Held as a ratio rather than the flat pixel count it used to be, because this module now draws at three
 * sizes — 128px style tiles, 512px previews and the 2048px download — and a flat 34px would be a quarter of a style tile.
 */
const QR_LOGO_MARGIN_RATIO = 34 / 2048;
const ALPHA_ROUNDING_FACTOR = 100;

/**
 * Matches `tinycolor(...).toRgbString()` — integer channels, `rgb()` when fully opaque and `rgba()` otherwise, alpha rounded to two decimals. Not imported from `@Utils/color.util`
 * because that module value-imports `@Components/color-fill`, which pulls React and a stylesheet into what has to stay a leaf module for a marketing-page bundle.
 */
const rgbaToCssString = (color: RGBA): string => {
  const [red, green, blue, alpha] = color;
  const channels = `${Math.round(red)}, ${Math.round(green)}, ${Math.round(blue)}`;
  if (alpha === 1) {
    return `rgb(${channels})`;
  }
  return `rgba(${channels}, ${Math.round(alpha * ALPHA_ROUNDING_FACTOR) / ALPHA_ROUNDING_FACTOR})`;
};

/**
 * Here for the same reason as `rgbaToCssString` above: `@Utils/color.util` owns this, and value-importing it pulls `@Components/color-fill` and the editor's side-panel types into
 * a marketing-page bundle.
 */
export const hexToRgba = (hex: string, alpha = 1): RGBA => {
  const decimal = parseInt(hex.includes('#') ? hex.substring(1) : hex, 16);
  return [decimal >> 16, (decimal & 0x00ff00) >> 8, decimal & 0x0000ff, alpha];
};

/** A QR code draws a gradient its own way, from the colors alone: spread evenly, top to bottom or from the center. */
const getQRCodeStylingFillColorConfig = (fill: FillObject): QRCodeStylingColorConfig => {
  const colors = getStoredFillColors(fill);
  if (hasGradientFill(fill)) {
    const isLinear = hasLinearGradientFill(fill);
    return {
      gradient: {
        type: isLinear ? 'linear' : 'radial',
        rotation: isLinear ? QR_LINEAR_GRADIENT_ROTATION : 0,
        colorStops: colors.map((color, index) => ({
          offset: colors.length > 1 ? index / (colors.length - 1) : 0,
          color: rgbaToCssString(color),
        })),
      },
    };
  }
  return {color: rgbaToCssString(colors[0])};
};

const getQRCodeStylingBackgroundColorConfig = (fill: FillObject): QRCodeStylingColorConfig => {
  if (!hasFill(fill)) {
    return {color: QR_TRANSPARENT_BACKGROUND_COLOR};
  }
  return getQRCodeStylingFillColorConfig(fill);
};

const getGenerationMarginPx = (marginPercent: number, sizePx: number): number => {
  return (marginPercent / QR_MARGIN_MAX) * sizePx * QR_MAX_MARGIN_FRACTION;
};

/**
 * The error-correction level is derived from the config rather than passed in, so every caller lands on the same level for the same code — a tool preview and the poster item it
 * hands off to must not disagree. A logo covers modules, so it needs level H's extra redundancy to stay scannable.
 */
export const getQRCodeStylingOptions = (config: QRRenderConfig): QRCodeStylingOptions => {
  const {logoDataUrl} = config;
  const foregroundColorConfig = getQRCodeStylingFillColorConfig(config.foregroundFill);
  const options: QRCodeStylingOptions = {
    width: config.sizePx,
    height: config.sizePx,
    margin: getGenerationMarginPx(config.marginPercent, config.sizePx),
    data: config.data,
    qrOptions: {
      errorCorrectionLevel: logoDataUrl !== null ? QR_ERROR_CORRECTION_LEVEL_WITH_LOGO : QR_ERROR_CORRECTION_LEVEL,
    },
    dotsOptions: {
      type: QR_DOT_TYPE_MAP[config.dotStyle],
      ...foregroundColorConfig,
    },
    cornersSquareOptions: {
      type: QR_EYE_FRAME_TYPE_MAP[config.eyeFrameStyle],
      ...foregroundColorConfig,
    },
    cornersDotOptions: {
      type: QR_EYE_BALL_TYPE_MAP[config.eyeBallStyle],
      ...foregroundColorConfig,
    },
    backgroundOptions: {
      round: config.backgroundCornerRadiusPercent / QR_BACKGROUND_CORNER_RADIUS_MAX,
      ...getQRCodeStylingBackgroundColorConfig(config.backgroundFill),
    },
  };

  if (logoDataUrl !== null) {
    options.image = logoDataUrl;
    options.imageOptions = {
      imageSize: config.logoSizeRatio,
      hideBackgroundDots: QR_LOGO_HIDE_BACKGROUND_DOTS,
      margin: QR_LOGO_MARGIN_RATIO * config.sizePx,
    };
  }

  return options;
};

export const renderQRCodeBlob = async (config: QRRenderConfig, extension: QRCodeFileExtension): Promise<Blob> => {
  const qrCodeStylingModule = await import('qr-code-styling');
  const qrCodeStyling = new qrCodeStylingModule.default(getQRCodeStylingOptions(config));
  const rawData = await qrCodeStyling.getRawData(extension);
  if (!(rawData instanceof Blob)) {
    // The payload is deliberately absent: it can be a Wi-Fi password or a whole contact card, and this
    // message reaches a logger. Its length is the part that actually explains a failed render.
    throw new Error(`Failed to generate a ${extension} QR code carrying ${config.data.length} characters`);
  }
  return rawData;
};

/**
 * A frame drawn around a finished code. `thickness` is the poster editor's own number for an item outline, so a code styled here and then opened in the editor carries the value
 * the visitor set rather than a translation of it.
 */
interface QROutline {
  thickness: number;
  color: RGBA;
  backgroundCornerRadiusPercent: number;
}

/** The editor states an outline's thickness as a count of poster pixels, which reads as a different weight on every item size. */
const QR_EDITOR_ITEM_TO_POSTER_WIDTH_RATIO = 0.3;
const QR_REFERENCE_POSTER_WIDTH_PX = 1080;
const QR_EDITOR_REFERENCE_ITEM_WIDTH_PX = QR_EDITOR_ITEM_TO_POSTER_WIDTH_RATIO * QR_REFERENCE_POSTER_WIDTH_PX;

/** The corner radius the code's own background is drawn with, as `qr-code-styling` reads it. */
const getBackgroundCornerRadiusPx = (backgroundCornerRadiusPercent: number, sizePx: number): number => {
  return (backgroundCornerRadiusPercent / QR_BACKGROUND_CORNER_RADIUS_MAX) * (sizePx / 2);
};

const getQROutlineWidthPx = (thickness: number, sizePx: number): number => {
  return (thickness / QR_EDITOR_REFERENCE_ITEM_WIDTH_PX) * sizePx;
};

export const hasQROutline = (thickness: number): boolean => {
  return thickness > 0;
};

/**
 * `CanvasRenderingContext2D.roundRect` is too new to rely on here — this page is a marketing page reached from search, on whatever phone the visitor has — so the path is built
 * from arcs.
 */
const traceRoundedRectangle = (context: CanvasRenderingContext2D, offset: number, side: number, radius: number): void => {
  const cappedRadius = Math.min(radius, side / 2);
  context.beginPath();
  context.moveTo(offset + cappedRadius, offset);
  context.arcTo(offset + side, offset, offset + side, offset + side, cappedRadius);
  context.arcTo(offset + side, offset + side, offset, offset + side, cappedRadius);
  context.arcTo(offset, offset + side, offset, offset, cappedRadius);
  context.arcTo(offset, offset, offset + side, offset, cappedRadius);
  context.closePath();
};

const loadImageFromBlob = async (blob: Blob): Promise<HTMLImageElement> => {
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = (): void => {
        resolve(image);
      };
      image.onerror = (): void => {
        reject(new Error('The rendered QR code could not be read back to draw its outline'));
      };
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const canvasToBlob = async (canvas: HTMLCanvasElement): Promise<Blob> => {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob): void => {
      if (blob === null) {
        reject(new Error('The outlined QR code could not be encoded'));
        return;
      }
      resolve(blob);
    });
  });
};

/**
 * The outline is stroked around the raster rather than over it, so the code grows by the outline on every side exactly as an outlined item does in the editor. The stroke is
 * centred on the raster's edge, which is why the drawing is inset by half the outline: a stroke straddling the canvas boundary would be clipped to half its width.
 */
const addOutlineToRasterQRCode = async (blob: Blob, outline: QROutline, sizePx: number): Promise<Blob> => {
  const outlineWidthPx = getQROutlineWidthPx(outline.thickness, sizePx);
  const image = await loadImageFromBlob(blob);
  const canvas = document.createElement('canvas');
  canvas.width = sizePx + outlineWidthPx * 2;
  canvas.height = canvas.width;
  const context = canvas.getContext('2d');
  if (context === null) {
    throw new Error('The outlined QR code could not be drawn: no 2d canvas context');
  }

  context.drawImage(image, outlineWidthPx, outlineWidthPx, sizePx, sizePx);
  context.lineWidth = outlineWidthPx;
  context.strokeStyle = rgbaToCssString(outline.color);
  const strokeInset = outlineWidthPx / 2;
  traceRoundedRectangle(context, strokeInset, sizePx + outlineWidthPx, getBackgroundCornerRadiusPx(outline.backgroundCornerRadiusPercent, sizePx) + strokeInset);
  context.stroke();
  return canvasToBlob(canvas);
};

/**
 * The rendered code becomes a nested viewport inside a larger one rather than being re-serialised: its own markup carries generated clip-path ids, and rewriting the root's
 * geometry around them risks changing what those refer to. A nested `<svg>` keeps the code exactly as generated.
 */
const addOutlineToVectorQRCode = async (blob: Blob, outline: QROutline, sizePx: number): Promise<Blob> => {
  const outlineWidthPx = getQROutlineWidthPx(outline.thickness, sizePx);
  const totalSizePx = sizePx + outlineWidthPx * 2;
  const strokeInset = outlineWidthPx / 2;
  const strokeSide = sizePx + outlineWidthPx;
  const cornerRadiusPx = getBackgroundCornerRadiusPx(outline.backgroundCornerRadiusPercent, sizePx) + strokeInset;
  const codeMarkup = await blob.text();
  const outlinedMarkup =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${totalSizePx}" height="${totalSizePx}" viewBox="0 0 ${totalSizePx} ${totalSizePx}">` +
    `<g transform="translate(${outlineWidthPx}, ${outlineWidthPx})">${codeMarkup}</g>` +
    `<rect x="${strokeInset}" y="${strokeInset}" width="${strokeSide}" height="${strokeSide}" rx="${cornerRadiusPx}" fill="none" ` +
    `stroke="${rgbaToCssString(outline.color)}" stroke-width="${outlineWidthPx}"/>` +
    `</svg>`;
  return new Blob([outlinedMarkup], {type: blob.type});
};

export const addOutlineToQRCode = async (blob: Blob, extension: QRCodeFileExtension, outline: QROutline, sizePx: number): Promise<Blob> => {
  if (extension === 'svg') {
    return addOutlineToVectorQRCode(blob, outline, sizePx);
  }
  return addOutlineToRasterQRCode(blob, outline, sizePx);
};
